/**
 * Alliance-system tuning. Pure helpers that consume these tables live in
 * `packages/game-engine/src/alliance.ts`, mirroring the
 * `constants/espionage.ts` / `game-engine/src/espionage.ts` split.
 */
/**
 * Founding or running an alliance needs the Team building (Command Center /
 * Regulator palace / Hive) — see `docs/game-details/buildings.md`: "This
 * political building is required to run and manage your alliance and Team
 * planet." Only creation is gated: demolishing the building afterwards does
 * not dissolve an existing alliance, same as every other one-shot
 * prerequisite in the game.
 */
export const ALLIANCE_BUILDING = "government";
/** Minimum {@link ALLIANCE_BUILDING} level on any owned planet to found an alliance. */
export const ALLIANCE_REQUIRED_BUILDING_LEVEL = 1;
/**
 * Hard cap on members. Chosen to sit above the ~30-40 an active alliance
 * reaches in the source game while staying inside a single Firestore
 * `getAll` batch when `getAlliance` joins members to their ranking rows.
 */
export const ALLIANCE_MAX_MEMBERS = 50;
/** Inclusive display-name bounds. Uniqueness is case-insensitive (`nameKey`). */
export const ALLIANCE_NAME_MIN_LENGTH = 3;
export const ALLIANCE_NAME_MAX_LENGTH = 32;
/** Inclusive tag bounds. Stored uppercased; uniqueness is case-insensitive (`tagKey`). */
export const ALLIANCE_TAG_MIN_LENGTH = 2;
export const ALLIANCE_TAG_MAX_LENGTH = 5;
export const ALLIANCE_DESCRIPTION_MAX_LENGTH = 1000;
/** Free-text note a player may attach to a join application. */
export const ALLIANCE_APPLICATION_MESSAGE_MAX_LENGTH = 300;
/**
 * Letters, digits, spaces, and a small punctuation set. Deliberately
 * excludes `/` and control characters so a name can never be mistaken for a
 * Firestore path segment, and excludes the zero-width/bidi range so a tag
 * can't be made to render as another alliance's.
 */
export const ALLIANCE_NAME_PATTERN = /^[\p{L}\p{N}][\p{L}\p{N} '._-]*$/u;
/** Tags are alphanumeric only — they render inline next to player names. */
export const ALLIANCE_TAG_PATTERN = /^[\p{L}\p{N}]+$/u;
/**
 * Least → most privileged. Compared numerically by `canManageMember` in
 * game-engine so "an officer may only act on someone strictly below them"
 * is one rule rather than a per-action branch.
 */
export const ALLIANCE_ROLE_RANK = {
    member: 0,
    officer: 1,
    leader: 2,
};
export const ALLIANCE_ROLES = ["member", "officer", "leader"];
export const ALLIANCE_ROLE_LABELS = {
    member: "Member",
    officer: "Officer",
    leader: "Leader",
};
/**
 * What each role may do. `leader` deliberately lists every permission
 * explicitly rather than inheriting by rank — a new permission should have
 * to be granted on purpose, not appear for the leader by accident.
 */
export const ALLIANCE_ROLE_PERMISSIONS = {
    member: [],
    officer: ["manageApplications", "kickMembers", "manageTeamPlanet"],
    leader: [
        "manageProfile",
        "manageApplications",
        "kickMembers",
        "manageRoles",
        "transferLeadership",
        "disband",
        "manageTeamPlanet",
    ],
};
/**
 * Pending applications an alliance may hold at once. Bounds the officer
 * review list, and — more importantly — bounds `/disbandAlliance`, which
 * deletes every member and application in a single transaction (Firestore
 * caps a transaction at 500 writes).
 */
export const ALLIANCE_MAX_PENDING_APPLICATIONS = 100;
/**
 * How many alliances one player may have applications out to at once.
 * Tracked as an id array on their own player doc rather than a
 * collection-group count, so applying/cancelling stays a single-document
 * check.
 */
export const ALLIANCE_MAX_APPLICATIONS_PER_PLAYER = 10;
/**
 * How many alliances `/listAlliances` returns per call, ordered by total
 * alliance points (battle + resource) descending.
 */
export const ALLIANCE_BROWSE_LIMIT = 50;
/**
 * Upper bound on the alliance-document scan behind `/listAlliances`.
 * Alliances are read ordered by `memberCount` descending and only then
 * sorted by alliance points, so a very small alliance in a universe with
 * more than this many alliances may not appear in the browse list until it
 * grows. Search runs over the same window.
 */
export const ALLIANCE_LIST_SCAN_LIMIT = 200;
/**
 * Contribution points (per member, internal to the alliance) for each unit
 * of ship population a member's Station delivers to the Team planet.
 * Resources delivered by Station or Transport count at the player-points
 * rate instead (`PLAYER_POINTS_DIVISOR` resources per point). Four points
 * per population sits inside the 2,000–6,650 resources a war ship's
 * population slot costs to build, so delivering ships and delivering the
 * resources to build them earn roughly the same. Provisional.
 */
export const ALLIANCE_CONTRIBUTION_POINTS_PER_POPULATION = 4;
//# sourceMappingURL=alliances.js.map